/**
 * Owns the "this drop" review filter: session file ids, URL
 * `?review=drop&batch=…`, and the live unverified queue for those files.
 */
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AttentionRow, ProvenanceMap } from '@/components/brand-bible/NeedsAttentionQueue';
import {
  DROP_BATCH_PARAM,
  DROP_REVIEW_PARAM,
  DROP_REVIEW_VALUE,
  countsByLetter,
  filterRowsByFileIds,
  loadDropBatch,
  newDropBatchId,
  persistDropBatch,
  uniqueIds,
} from './dropReview';

export function useBbDropReview({
  toVerifyRows,
  draftRows,
  provenance,
}: {
  toVerifyRows: AttentionRow[];
  draftRows: AttentionRow[];
  provenance?: ProvenanceMap;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [draftBatchId, setDraftBatchId] = useState<string | null>(null);
  const [localFilesByBatch, setLocalFilesByBatch] = useState<Record<string, string[]>>({});

  const urlReview = searchParams.get(DROP_REVIEW_PARAM);
  const urlBatch = searchParams.get(DROP_BATCH_PARAM);
  const filterActive = urlReview === DROP_REVIEW_VALUE && Boolean(urlBatch);
  const batchId = urlBatch ?? draftBatchId;
  const storedFileIds = useMemo(() => (batchId ? loadDropBatch(batchId) : []), [batchId]);
  const fileIds = useMemo(
    () => uniqueIds([...storedFileIds, ...(batchId ? localFilesByBatch[batchId] ?? [] : [])]),
    [batchId, localFilesByBatch, storedFileIds],
  );

  const unverified = useMemo(
    () => [...toVerifyRows, ...draftRows],
    [toVerifyRows, draftRows],
  );

  const dropQueue = useMemo(
    () => filterRowsByFileIds(unverified, provenance, fileIds),
    [unverified, provenance, fileIds],
  );

  const byLetter = useMemo(() => countsByLetter(dropQueue), [dropQueue]);

  const rememberFiles = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      const id = batchId ?? newDropBatchId();
      if (!batchId) setDraftBatchId(id);
      setLocalFilesByBatch((prev) => {
        const next = uniqueIds([...(prev[id] ?? loadDropBatch(id)), ...ids]);
        persistDropBatch(id, next);
        return { ...prev, [id]: next };
      });
    },
    [batchId],
  );

  const writeUrl = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(DROP_REVIEW_PARAM, DROP_REVIEW_VALUE);
          next.set(DROP_BATCH_PARAM, id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const activateFilter = useCallback(
    (ids?: string[]) => {
      const nextIds = uniqueIds(ids?.length ? ids : fileIds);
      if (nextIds.length === 0) return;
      const id = batchId ?? newDropBatchId();
      setDraftBatchId(id);
      setLocalFilesByBatch((prev) => ({ ...prev, [id]: nextIds }));
      persistDropBatch(id, nextIds);
      writeUrl(id);
    },
    [batchId, fileIds, writeUrl],
  );

  const clearFilter = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(DROP_REVIEW_PARAM);
        next.delete(DROP_BATCH_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  return {
    fileIds,
    dropQueue,
    remaining: dropQueue.length,
    byLetter,
    filterActive,
    rememberFiles,
    activateFilter,
    clearFilter,
  };
}
